/**
 * Debug logger for the print pipeline.
 *
 * Why this exists: Chrome's native "Print Failed — Something went wrong when
 * trying to print" dialog gives zero diagnostic info. Our print dispatchers
 * (customer receipt, KOT, PRA invoice, Z-report, FBR credit note) all share an
 * off-screen iframe + `win.print()` + `setTimeout(cleanup)` pattern and all
 * swallow errors silently. This module gives every step a console trace so the
 * exact failure point is visible in DevTools.
 *
 * Toggle: enabled by default. To silence, set `localStorage.print_debug = '0'`.
 * To enable verbose stack traces on caught errors, set `print_debug = 'v'`.
 *
 * No behaviour change — purely observational.
 */

type Level = 'info' | 'warn' | 'error'

function enabled(): boolean {
  try {
    return localStorage.getItem('print_debug') !== '0'
  } catch {
    return true
  }
}

function verbose(): boolean {
  try {
    return localStorage.getItem('print_debug') === 'v'
  } catch {
    return false
  }
}

function ts(): string {
  const d = new Date()
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  const ss = String(d.getSeconds()).padStart(2, '0')
  const ms = String(d.getMilliseconds()).padStart(3, '0')
  return `${hh}:${mm}:${ss}.${ms}`
}

function emit(level: Level, tag: string, msg: string, data?: unknown): void {
  if (!enabled()) return
  const prefix = `%c[print:${tag}]%c ${ts()} ${msg}`
  const tagStyle = 'color:#0a7d8b;font-weight:600'
  const reset = 'color:inherit;font-weight:inherit'
  const args: unknown[] = [prefix, tagStyle, reset]
  if (data !== undefined) args.push(data)
  // eslint-disable-next-line no-console
  ;(console[level === 'info' ? 'log' : level] as (...a: unknown[]) => void)(...args)
}

export const printDebug = {
  log(tag: string, msg: string, data?: unknown): void {
    emit('info', tag, msg, data)
  },
  warn(tag: string, msg: string, data?: unknown): void {
    emit('warn', tag, msg, data)
  },
  error(tag: string, msg: string, err: unknown): void {
    const payload =
      err instanceof Error
        ? { name: err.name, message: err.message, stack: verbose() ? err.stack : undefined }
        : err
    emit('error', tag, msg, payload)
  },
  /**
   * Wraps a synchronous job with timing. Logs entry, completion, and any
   * thrown error.
   */
  span<T>(tag: string, label: string, fn: () => T): T {
    if (!enabled()) return fn()
    const t0 = performance.now()
    emit('info', tag, `▶ ${label}`)
    try {
      const out = fn()
      emit('info', tag, `✓ ${label} (${(performance.now() - t0).toFixed(1)}ms)`)
      return out
    } catch (err) {
      emit('error', tag, `✗ ${label} (${(performance.now() - t0).toFixed(1)}ms)`, err)
      throw err
    }
  },
  /**
   * Wraps an async job with timing.
   */
  async spanAsync<T>(tag: string, label: string, fn: () => Promise<T>): Promise<T> {
    if (!enabled()) return fn()
    const t0 = performance.now()
    emit('info', tag, `▶ ${label}`)
    try {
      const out = await fn()
      emit('info', tag, `✓ ${label} (${(performance.now() - t0).toFixed(1)}ms)`)
      return out
    } catch (err) {
      emit('error', tag, `✗ ${label} (${(performance.now() - t0).toFixed(1)}ms)`, err)
      throw err
    }
  },
  /**
   * Attaches `beforeprint` / `afterprint` / `error` listeners to a print iframe
   * window so we can see whether Chrome actually started + finished the print
   * job. Crucial for diagnosing the "iframe removed before Chrome captured the
   * document" race that produces the native "Print Failed" dialog.
   */
  attachWindowProbes(tag: string, win: Window, doc: Document): void {
    if (!enabled()) return
    const t0 = performance.now()
    const elapsed = () => `${(performance.now() - t0).toFixed(1)}ms`

    try {
      win.addEventListener('beforeprint', () => {
        emit('info', tag, `🖨  beforeprint fired (${elapsed()} after probe attach)`)
      })
      win.addEventListener('afterprint', () => {
        emit('info', tag, `🖨  afterprint fired (${elapsed()} after probe attach)`)
      })
      win.addEventListener('error', (ev) => {
        emit('error', tag, 'window error inside print iframe', {
          message: ev.message,
          filename: ev.filename,
          lineno: ev.lineno,
          colno: ev.colno,
        })
      })
      win.addEventListener('unhandledrejection', (ev) => {
        emit('error', tag, 'unhandled rejection inside print iframe', ev.reason)
      })
      doc.addEventListener('readystatechange', () => {
        emit('info', tag, `doc.readyState → ${doc.readyState}`)
      })
    } catch (err) {
      emit('warn', tag, 'failed to attach window probes', err)
    }
  },
  /**
   * Snapshot the iframe/doc state for a single log line — useful right before
   * `win.print()` so we know what Chrome will try to print.
   */
  snapshot(tag: string, label: string, doc: Document | null, win: Window | null): void {
    if (!enabled()) return
    if (!doc || !win) {
      emit('warn', tag, `${label}: doc/win is null`, { doc: !!doc, win: !!win })
      return
    }
    emit('info', tag, label, {
      readyState: doc.readyState,
      title: doc.title,
      bodyChildren: doc.body?.children.length ?? 0,
      bodyScrollHeight: doc.body?.scrollHeight ?? 0,
      docScrollHeight: doc.documentElement?.scrollHeight ?? 0,
      images: doc.images.length,
      imagesComplete: Array.from(doc.images).filter((i) => i.complete).length,
      stylesheets: doc.styleSheets.length,
    })
  },
}

export type PrintDebug = typeof printDebug
